import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DEV_BYPASS_AUTH } from "@/lib/auth-config";
import { BACKEND_URL, supabase } from "@/lib/supabase";
import { apiFetch, waitForAccessToken } from "@/lib/api";
import { FetchEventSource } from "@/lib/sse";
import { COUNCIL } from "@/lib/council";
import { modelToPersona, resolvePersona } from "@/lib/persona-mapping";
import { guestFromPayload, isGuestKey, type GuestInfo } from "@/lib/guest-pool";

import { parseGreffierBriefing } from "@/lib/parse-briefing";
import { announce } from "@/lib/announce";
import type {
  ConnectionState,
  GreffierBriefingStructured,
  PersonaName,
  PersonaResearch,
  PersonaResearchSource,
  SearchEvent,
} from "@/types/personas";

import type {
  InvestigationProgress,
  InvestigationSource,
  ResearchQuery,
} from "@/components/Deliberation/InvestigationLive";

const PERSONA_NAME_BY_ID: Record<string, string> = Object.fromEntries(
  COUNCIL.map((c) => [c.id, c.name]),
);

/**
 * Normalise un payload de message Conseil en string HTML-safe.
 * Le backend peut renvoyer soit une string, soit un objet structuré
 * { synthese, key_points[], divergences[] }. Sans cette normalisation,
 * React crashe (#31) quand on essaie de rendre l'objet directement.
 */
function normalizeBubbleContent(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (typeof raw === "object") {
    const obj = raw as {
      synthese?: unknown;
      key_points?: unknown;
      divergences?: unknown;
      content?: unknown;
      text?: unknown;
    };
    // Cas imbriqué : { content: "..." } ou { text: "..." }
    if (typeof obj.content === "string") return obj.content;
    if (typeof obj.text === "string") return obj.text;

    const parts: string[] = [];
    if (typeof obj.synthese === "string" && obj.synthese.trim()) {
      parts.push(obj.synthese.trim());
    }
    if (Array.isArray(obj.key_points) && obj.key_points.length > 0) {
      const items = obj.key_points
        .filter((kp): kp is string => typeof kp === "string" && kp.trim().length > 0)
        .map((kp) => `• ${kp}`)
        .join("\n");
      if (items) parts.push(`\n<em>Points clés</em>\n${items}`);
    }
    if (Array.isArray(obj.divergences) && obj.divergences.length > 0) {
      const items = obj.divergences
        .filter((d): d is string => typeof d === "string" && d.trim().length > 0)
        .map((d) => `• ${d}`)
        .join("\n");
      if (items) parts.push(`\n<em>Divergences</em>\n${items}`);
    }
    if (parts.length > 0) return parts.join("\n");
    // Dernier recours : JSON brut pour ne pas perdre l'info
    try {
      return JSON.stringify(raw);
    } catch {
      return "";
    }
  }
  return String(raw);
}

function safeString(raw: unknown, fallback = ""): string {
  if (raw === null || raw === undefined) return fallback;
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  return fallback;
}

function safeNumber(raw: unknown, fallback = 0): number {
  const value = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : NaN;
  return Number.isFinite(value) ? value : fallback;
}

function safeStringArray(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((item): item is string => typeof item === "string") : [];
}

function safeRecordArray(raw: unknown): Array<Record<string, unknown>> {
  return Array.isArray(raw)
    ? raw.filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}


export type OrbPhase = "idle" | "summon" | "debate" | "converge" | "verdict";
// PersonaKey acceptait historiquement uniquement les 7 personas du Conseil.
// On l'élargit pour accepter les invités (key `expert-<uuid8>`) sans perdre
// la complétion sur les noms canoniques.
export type PersonaKey = PersonaName | (string & {});
export type BubbleState =
  | "pending"
  | "typing"
  | "streaming"
  | "complete"
  | "waiting"
  | "active";

export interface BubbleData {
  persona: PersonaKey;
  state: BubbleState;
  text: string;
  time: string;
  round?: number;
  trustworthiness?: number;
  abstained?: boolean;
}

export interface ChatMessage {
  id: string;
  persona: PersonaKey;
  state: BubbleState;
  text: string;
  time: string;
  round?: number;
  trustworthiness?: number;
  abstained?: boolean;
}


export interface VerifActivity {
  id: string;
  cadre: string;
  angle: string;
  sujet: string;
  pointsReleves: number;
  receivedAt: number;
}

export interface VerifExchange {
  id: string;
  from: string;
  to: string;
  pointsConfrontes: number;
  receivedAt: number;
}

export interface DeliberationState {
  id: string;
  question: string;
  mode: string;
  branch: "business" | "tendances" | "audit" | "verif" | null;
  saisineNumber: string;
  saisineDate: string;
  saisineTime: string;
  currentPhase: OrbPhase;
  currentTurn: number;
  totalTurns: number;
  bubbles: BubbleData[];
  messages: ChatMessage[];
  // S06-corrective additions
  searches: SearchEvent[];
  greffierBriefingRaw: unknown;
  greffierBriefing: GreffierBriefingStructured | null;
  turnOrder: PersonaName[];
  expectedTurns: number;
  connectionState: ConnectionState;
  verdictId: string | null;
  isCompleted: boolean;
  /** Verdict rendu (event SSE `verdict`) — affiche badge "Verdict prêt". */
  verdictReady: boolean;
  /** Réunion complètement terminée (event SSE `meeting_done`) — redirect dans 3s. */
  meetingDone: boolean;
  /** R4 Clarity Opus réussi (vs soft-fail) — pour layout adaptatif côté verdict. */
  clarityReady: boolean;
  /** Délibération bloquée : timeout 120s sans complete OU fatal_error explicite. */
  streamStalled: boolean;
  /**
   * Séance ÉCHOUÉE/interrompue de façon terminale (status DB `failed` OU event
   * SSE `error severity:fatal`) : aucun verdict ne viendra. Distinct de
   * `streamStalled` (qui suppose « encore en cours ») — ici on AVOUE l'échec et
   * on propose la relance. Doctrine : un échec s'affiche, jamais un reset muet.
   */
  streamFailed: boolean;
  /** Message d'échec à afficher (errorMessage backend ou raison SSE). */
  streamFailReason: string | null;
  investigationSources: InvestigationSource[];
  investigationProgress: InvestigationProgress;
  /** Requêtes de recherche émises au fil de l'eau (event SSE `research_query`). */
  researchQueries: ResearchQuery[];
  /**
   * Invités du tour — 8e siège(s) ajoutés via SSE `expert_joined`.
   * Clé : `expert-<uuid8>` (même canal que les experts custom).
   */
  guests: Record<string, GuestInfo>;
  /** Si non null, cette saisine est une relance d'un débat antérieur. */
  parentConversationId: string | null;
  /** Recherches par cadre (events SSE `persona_research_*`). Clé = clé modèle (claude/gpt/…). */
  personaResearch: Record<string, PersonaResearch>;
  /** Branche Vérif uniquement : fil d'activité du Conseil (events `verif_activity`). */
  verifActivities: VerifActivity[];
  /** Vérif : grounding juridique (event `verif_grounding_legal`). null si pas de droit. */
  verifGroundingLegal: { articles: number; decisions: number } | null;
  /** Vérif : fact-check web (event `verif_factcheck`). */
  verifFactcheck: { verifies: number; confirmees: number } | null;
  /** Vérif : confrontations entre cadres (event `verif_exchange`). */
  verifExchanges: VerifExchange[];
}

// Ordre du tour de table (Acte I) : Henri → Delphine → Julien → Akilah → Eva → Ren → Imani
const TURN_ORDER: PersonaKey[] = ["henri", "delphine", "julien", "akilah", "eva", "ren", "imani"];

const MOCK_TEXTS: Record<PersonaKey, string> = {
  henri:
    "Sur le plan financier, le ratio brûlé/recettes reste tenable jusqu'au prochain trimestre. <em>Prudence sur l'allocation du runway</em> — je recommande un buffer de six mois avant tout nouvel engagement.",
  delphine:
    "Stratégiquement, l'opportunité est réelle mais la fenêtre se referme. <em>Le timing prime sur la perfection</em> : mieux vaut un MVP imparfait livré ce trimestre qu'un produit poli au prochain.",
  julien:
    "Côté opérations, la séquence d'exécution est tenable si l'on découpe en sprints de deux semaines. <em>Identifions trois jalons mesurables</em> avant fin juin.",
  akilah:
    "Juridiquement, deux risques à clarifier : la propriété intellectuelle des modèles entraînés et la conformité RGPD sur les données sensibles. <em>Un avis préalable s'impose</em>.",
  eva: "Côté communication, le récit est puissant mais demande à être incarné. <em>Une seule voix, une seule promesse</em> — ne dispersons pas le message sur plusieurs angles.",
  ren: "Permettez-moi de jouer l'avocat du diable : <em>et si la demande n'était qu'une mode passagère ?</em> Le pivot doit rester accessible si les signaux faibles s'inversent.",
  imani:
    "Côté capital humain : la traction se gagne aussi par les équipes. <em>Cadrons les trois recrutements critiques</em> et le rituel d'onboarding avant tout passage à l'échelle.",
};

const MOCK_BRIEFING_RAW = JSON.stringify({
  acte_1:
    "Le Président cherche à valider l'opportunité d'une levée de 500K€ en seed maintenant, à un stade où la traction commerciale reste modeste mais le marché fenêtre.",
  acte_2:
    "Trois axes de tension identifiés : valorisation pré-money vs dilution, calendrier macro (taux + sentiment VC européen), et la maturité des metrics SaaS pour soutenir l'argumentaire de growth.",
  acte_3:
    "Le Conseil devra trancher entre une levée immédiate à valorisation plancher et une attente conditionnée à l'atteinte de jalons commerciaux mesurables sur 6 mois.",
  consensus_emerging:
    "Aucun consensus pré-débat. Position initiale balancée 50/50.",
  open_questions: [
    "Quel est le runway actuel et le burn rate mensuel ?",
    "Y a-t-il des term sheets ouvertes en parallèle ?",
    "L'équipe peut-elle absorber une levée + un recrutement immédiat ?",
  ],
});

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
}

function initialBubbles(): BubbleData[] {
  return TURN_ORDER.map((p) => ({
    persona: p,
    state: "pending" as BubbleState,
    text: "",
    time: "",
  }));
}

export function mapSsePhase(p: string): OrbPhase {
  switch (p) {
    case "convocation":
      return "summon";
    case "tour_de_table":
      return "debate";
    case "convergence":
      return "converge";
    case "verdict_rendu":
      return "verdict";
    default:
      return "idle";
  }
}

const MOCK_SEARCHES_INIT: SearchEvent[] = [
  {
    id: "s-q-1",
    persona: "julien",
    query: "benchmark SaaS burn multiple seed 2026",
    status: "running",
    timestamp: new Date().toISOString(),
  },
  {
    id: "s-src-1",
    persona: "julien",
    source_url: "https://crunchbase.com/research/saas-burn-multiples-2026",
    source_title: "SaaS Burn Multiples · Q2 2026 Benchmark · Crunchbase Research",
    status: "done",
    timestamp: new Date().toISOString(),
  },
];

export interface DeliberationHookResult extends DeliberationState {
  displayedMessages: ChatMessage[];
  displayedCount: number;
  queuedCount: number;
  pace: number;
  setPace: (n: number) => void;
  accelerate: () => void;
  flushQueue: () => void;
  /** Reconnecte le SSE de la conversation en cours sans recharger la page. */
  reconnect: () => void;
}

export function useDeliberation(
  id: string,
  opts: { archiveMode?: boolean } = {},
): DeliberationHookResult {
  const archiveMode = !!opts.archiveMode;
  const navigate = useNavigate();
  const [state, setState] = useState<DeliberationState>(() => ({
    id,
    question: "Comment prioriser notre roadmap produit pour les six prochains mois ?",
    mode: "Validation d'idée",
    branch: null,
    saisineNumber: `MMXXVI·${id.padStart(3, "0").slice(-3)}`,
    saisineDate: "",
    saisineTime: "",
    currentPhase: "summon",
    currentTurn: 0,
    totalTurns: TURN_ORDER.length,
    bubbles: initialBubbles(),
    messages: [],
    searches: DEV_BYPASS_AUTH ? MOCK_SEARCHES_INIT : [],
    greffierBriefingRaw: DEV_BYPASS_AUTH ? MOCK_BRIEFING_RAW : null,
    greffierBriefing: DEV_BYPASS_AUTH ? parseGreffierBriefing(MOCK_BRIEFING_RAW) : null,
    turnOrder: TURN_ORDER as PersonaName[],
    expectedTurns: 18,
    connectionState: DEV_BYPASS_AUTH ? "live" : "connecting",
    verdictId: null,
    isCompleted: false,
    verdictReady: false,
    meetingDone: false,
    clarityReady: false,
    streamStalled: false,
    streamFailed: false,
    streamFailReason: null,
    investigationSources: [],
    investigationProgress: { total: 0, byCategory: {} },
    researchQueries: [],
    guests: {},
    parentConversationId: null,
    personaResearch: {},
    verifActivities: [],
    verifGroundingLegal: null,
    verifFactcheck: null,
    verifExchanges: [],
  }));

  // Hydration-safe : init des dates côté client uniquement
  useEffect(() => {
    setState((s) =>
      s.saisineDate
        ? s
        : {
            ...s,
            saisineDate:
              new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) +
              " MMXXVI",
            saisineTime: nowHHMM(),
          },
    );
  }, []);

  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Race-condition guard pour les relances : fenêtre "early fatal" + retries silencieux.
  const sseOpenedAtRef = useRef<number>(0);
  const earlyFailRetryCountRef = useRef<number>(0);
  const [reconnectNonce, setReconnectNonce] = useState(0);

  const reconnect = () => {
    earlyFailRetryCountRef.current = 0;
    setState((s) => ({
      ...s,
      streamStalled: false,
      streamFailed: false,
      streamFailReason: null,
      connectionState: "connecting",
    }));
    setReconnectNonce((n) => n + 1);
  };

  // ── Rendu immédiat : le backend pace déjà les bulles (BUBBLE_PACING_MS=1200ms).
  //    Toute file d'attente côté front recréerait le bug "19 prises de parole
  //    en attente". On affiche chaque message SSE dès qu'il arrive.
  const [pace, setPace] = useState(0);
  const displayedCount = state.messages.length;
  const displayedMessages = state.messages;
  const queuedCount = 0;
  const accelerate = () => setPace(0);
  const flushQueue = () => {};
  void archiveMode;


  useEffect(() => {
    // ── Mode Archive : pas de SSE, pas de simulation, juste hydrater depuis history ──
    if (archiveMode) {
      let cancelled = false;
      (async () => {
        if (DEV_BYPASS_AUTH) {
          // Mock complet, instantané, toutes bulles "spoke"
          const mockMsgs: ChatMessage[] = TURN_ORDER.map((persona, idx) => ({
            id: `archive-mock-${persona}-${idx}`,
            persona,
            state: "complete",
            text: MOCK_TEXTS[persona],
            time: nowHHMM(),
            round: 1,
          }));
          setState((s) => ({
            ...s,
            currentPhase: "verdict",
            currentTurn: TURN_ORDER.length,
            bubbles: TURN_ORDER.map((p) => ({
              persona: p,
              state: "complete" as BubbleState,
              text: MOCK_TEXTS[p],
              time: nowHHMM(),
            })),
            messages: mockMsgs,
            connectionState: "live",
            isCompleted: true,
          }));
          return;
        }
        try {
          const token = await waitForAccessToken();
          if (!token) {
            if (!cancelled) setState((s) => ({ ...s, connectionState: "lost" }));
            return;
          }
          const history = await apiFetch<{ conversation?: Record<string, unknown> }>(
            `/api/chat/${id}/history`,
          );
          if (cancelled) return;
          const conv = (history?.conversation ?? {}) as Record<string, unknown>;
          const turnOrderHuman = safeStringArray(conv.turn_order)
            .map((m: string) => modelToPersona(m)) as PersonaName[];
          const briefingRaw = conv.greffier_briefing ?? null;
          const rawMessages = safeRecordArray(conv.messages);
          const messages: ChatMessage[] = rawMessages.map((m, idx) => {
            const persona = modelToPersona(
              safeString(m.persona, safeString(m.model, "claude")),
            );
            return {
              id: safeString(m.id, `archive-${idx}`),
              persona,
              state: "complete" as BubbleState,
              text: normalizeBubbleContent(m.content ?? m.text),
              time: safeString(m.time, nowHHMM()),
              round: safeNumber(m.round, 1),
              trustworthiness: typeof m.trustworthiness === "number" ? m.trustworthiness : undefined,
            };
          });
          const sources = safeRecordArray(conv.research_sources)
            .map((src, idx): SearchEvent => ({
              id: `archive-src-${idx}`,
              persona: "julien",
              source_url: safeString(src.url),
              source_title: safeString(src.title, safeString(src.url)),
              snippet: safeString(src.snippet) || undefined,
              status: "done",
              timestamp: safeString(src.timestamp, new Date().toISOString()),
            }));
          setState((s) => ({
            ...s,
            mode: safeString(conv.mode, s.mode),
            greffierBriefingRaw: briefingRaw,
            greffierBriefing: parseGreffierBriefing(briefingRaw),
            saisineNumber: safeString(conv.saisine_number, s.saisineNumber),
            question: safeString(conv.question, s.question),
            parentConversationId: safeString(conv.parent_conversation_id) || null,
            turnOrder: turnOrderHuman.length === 6 ? turnOrderHuman : s.turnOrder,
            expectedTurns: safeNumber(conv.expected_turns, s.expectedTurns),
            messages,
            bubbles: (turnOrderHuman.length === 6 ? turnOrderHuman : TURN_ORDER).map(
              (p) => ({
                persona: p,
                state: "complete" as BubbleState,
                text: messages.find((m) => m.persona === p)?.text ?? "",
                time: nowHHMM(),
              }),
            ),
            searches: sources,
            currentPhase: "verdict",
            currentTurn: TURN_ORDER.length,
            connectionState: "live",
            isCompleted: true,
          }));
        } catch {
          if (!cancelled) setState((s) => ({ ...s, connectionState: "lost" }));
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (DEV_BYPASS_AUTH) {
      const t = (ms: number, fn: () => void) => {
        const tid = setTimeout(fn, ms);
        timeoutsRef.current.push(tid);
      };

      t(3000, () => setState((s) => ({ ...s, currentPhase: "debate" })));

      TURN_ORDER.forEach((persona, idx) => {
        const base = 3500 + idx * 2200;
        const msgId = `mock-${persona}-${idx}`;
        t(base, () => {
          setState((s) => ({
            ...s,
            currentTurn: idx + 1,
            bubbles: s.bubbles.map((b, i) =>
              i === idx
                ? { ...b, state: "typing", time: nowHHMM() }
                : i < idx
                ? b
                : { ...b, state: "waiting" },
            ),
            messages: [
              ...s.messages,
              { id: msgId, persona, state: "typing", text: "", time: nowHHMM(), round: 1 },
            ],
          }));
        });
        t(base + 700, () => {
          setState((s) => ({
            ...s,
            bubbles: s.bubbles.map((b, i) =>
              i === idx ? { ...b, state: "streaming", text: MOCK_TEXTS[persona] } : b,
            ),
            messages: s.messages.map((m) =>
              m.id === msgId ? { ...m, state: "streaming", text: MOCK_TEXTS[persona] } : m,
            ),
          }));
        });
        t(base + 1700, () => {
          setState((s) => ({
            ...s,
            bubbles: s.bubbles.map((b, i) => (i === idx ? { ...b, state: "complete" } : b)),
            messages: s.messages.map((m) =>
              m.id === msgId ? { ...m, state: "complete" } : m,
            ),
          }));
        });
      });

      const convergeAt = 3500 + TURN_ORDER.length * 2200 + 200;
      t(convergeAt, () => setState((s) => ({ ...s, currentPhase: "converge" })));
      t(convergeAt + 3000, () => setState((s) => ({ ...s, currentPhase: "verdict" })));

      return () => {
        timeoutsRef.current.forEach(clearTimeout);
        timeoutsRef.current = [];
      };
    }

    // Mode réel : history + SSE
    let es: FetchEventSource | null = null;
    let cancelled = false;

    (async () => {
      try {
        // 1. Fetch history pour mode + briefing + turn_order
        // Attendre l'hydratation de la session (cold-load) avant tout appel → pas de 401 transitoire.
        const token = await waitForAccessToken();
        if (!token) {
          setState((s) => ({ ...s, connectionState: "lost" }));
          return;
        }

        try {
          const history = await apiFetch<{ conversation?: Record<string, unknown> }>(
            `/api/chat/${id}/history`,
          );
          const conv = (history?.conversation ?? {}) as Record<string, unknown>;
          const turnOrderHuman = safeStringArray(conv.turn_order)
            .map((m: string) => modelToPersona(m)) as PersonaName[];
          const briefingRaw = conv.greffier_briefing ?? null;
          if (cancelled) return;
          const rawBranch = safeString(conv.branch);
          const branch: DeliberationState["branch"] =
            rawBranch === "business" || rawBranch === "tendances" || rawBranch === "audit" || rawBranch === "verif"
              ? rawBranch
              : null;
          setState((s) => ({
            ...s,
            mode: safeString(conv.mode, s.mode),
            branch: branch ?? s.branch,
            greffierBriefingRaw: briefingRaw,
            greffierBriefing: parseGreffierBriefing(briefingRaw),
            saisineNumber: safeString(conv.saisine_number, s.saisineNumber),
            question: safeString(conv.question, s.question),
            parentConversationId: safeString(conv.parent_conversation_id) || null,
            turnOrder: turnOrderHuman.length === 6 ? turnOrderHuman : s.turnOrder,
            expectedTurns: safeNumber(conv.expected_turns, s.expectedTurns),
            isCompleted: safeString(conv.status) === "completed",
          }));
        } catch {
          /* history optional */
        }

        if (cancelled) return;

        // 2. Open SSE — via fetch-SSE (FetchEventSource) avec le JWT en en-tête Authorization, JAMAIS en
        // query string (P0-quater ③ : le token ne doit plus apparaître dans les URLs du network). Le backend
        // accepte déjà le header (verifyTokenFromQuery → fallback Authorization).
        const url = `${BACKEND_URL}/api/public/bk/api/chat/${id}/stream`;
        es = new FetchEventSource(url, { headers: { Authorization: `Bearer ${token}` } });
        sseOpenedAtRef.current = Date.now();

        // ── Safe JSON parser : un event partiel/heartbeat ne doit JAMAIS crasher React.
        const safeParse = (raw: string): Record<string, unknown> | null => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        };

        // ── Stall watchdog (180s sans complete_conversation_done) ──
        const STALL_MS = 180_000;
        // ── Early-fatal window : sur une relance, le backend peut émettre un
        // event fatal "race" avant que le pipeline soit réellement amorcé.
        // On NE monte PAS l'écran "stalled" dans cette fenêtre — on retry en silence.
        const EARLY_FATAL_MS = 3_000;
        const EARLY_FATAL_MAX_RETRIES = 2;
        const EARLY_FATAL_RETRY_DELAY_MS = 1_500;
        let stallTimer: ReturnType<typeof setTimeout> | null = null;
        let pollTimer: ReturnType<typeof setInterval> | null = null;
        let pollSafetyTimer: ReturnType<typeof setTimeout> | null = null;
        let completed = false;

        const stopWatchdogs = () => {
          if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
          if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
          if (pollSafetyTimer) { clearTimeout(pollSafetyTimer); pollSafetyTimer = null; }
        };
        const armStall = () => {
          if (stallTimer) clearTimeout(stallTimer);
          stallTimer = setTimeout(() => {
            if (!completed && !cancelled) {
              setState((s) => ({ ...s, streamStalled: true }));
            }
          }, STALL_MS);
        };
        armStall();

        // Gestion centralisée d'un event fatal SSE (fatal_error, pipeline_aborted,
        // conversation_failed, error severity:fatal). Si l'event tombe dans la
        // fenêtre early-fatal d'une relance ET qu'il reste des retries → silent
        // retry (ferme + ré-ouvre via reconnectNonce après un court délai).
        // Sinon → vrai stall, on monte l'écran.
        const handleFatal = (evName: string, reason?: string) => {
          if (cancelled || completed) return;
          const elapsed = Date.now() - sseOpenedAtRef.current;
          if (
            elapsed < EARLY_FATAL_MS &&
            earlyFailRetryCountRef.current < EARLY_FATAL_MAX_RETRIES
          ) {
            earlyFailRetryCountRef.current += 1;
            // eslint-disable-next-line no-console
            console.warn(
              `[deliberation] early fatal ${evName} (${elapsed}ms) — silent retry #${earlyFailRetryCountRef.current}`,
            );
            stopWatchdogs();
            es?.close();
            setTimeout(() => {
              if (cancelled) return;
              setReconnectNonce((n) => n + 1);
            }, EARLY_FATAL_RETRY_DELAY_MS);
            return;
          }
          // Fenêtre early-fatal dépassée (ou retries épuisés) : échec TERMINAL.
          // On AVOUE l'échec (streamFailed, écran « interrompue — relancer »)
          // plutôt que streamStalled (« prend plus de temps », trompeur ici).
          // Doctrine : un échec s'affiche, jamais un reset muet.
          completed = true;
          stopWatchdogs();
          es?.close();
          setState((s) => ({
            ...s,
            streamFailed: true,
            streamFailReason:
              reason ??
              s.streamFailReason ??
              "La séance a été interrompue avant son terme — aucun verdict n'a été rendu.",
            streamStalled: false,
            connectionState: "lost",
          }));
        };

        // ── Polling fallback : si l'EventSource décroche, on demande l'état au backend.
        const pollStatus = async () => {
          try {
            const h = await apiFetch<{ conversation?: Record<string, unknown> }>(
              `/api/chat/${id}/history`,
            );
            const conv = h?.conversation as Record<string, unknown> | undefined;
            const status = conv?.status;
            if (status === "completed" && !cancelled) {
              // Backend marque la conversation "completed" dès que le verdict
              // est prêt — MAIS le débat live peut encore dérouler ses dernières
              // bulles côté UI. On expose juste `verdictReady` (badge) sans
              // forcer la redirection : c'est `meeting_done` (ou un clic user)
              // qui décide quand quitter l'écran de délibération.
              setState((s) => ({ ...s, verdictReady: true, verdictId: s.verdictId ?? id }));
            } else if (status === "failed" && !cancelled) {
              // Source AUTORITAIRE de l'échec (la base). Le stream a décroché et
              // la conv est marquée `failed` (salvage/watchdog OU crash) : aucun
              // verdict ne viendra. On AVOUE — état terminal `streamFailed`, on
              // coupe les watchdogs (plus de timer qui tourne dans le vide) et on
              // propose la relance. (P0 relance, symptôme 98c130db.)
              completed = true;
              stopWatchdogs();
              es?.close();
              const reason =
                typeof conv?.errorMessage === "string" && conv.errorMessage.trim()
                  ? (conv.errorMessage as string)
                  : "La séance a été interrompue avant son terme — aucun verdict n'a été rendu.";
              setState((s) => ({
                ...s,
                streamFailed: true,
                streamFailReason: reason,
                streamStalled: false,
                connectionState: "lost",
              }));
            }
          } catch {
            /* silent — polling reessaiera */
          }
        };
        const startPolling = () => {
          if (pollTimer) return;
          pollTimer = setInterval(pollStatus, 5000);
          // Cap polling at 180s; after that the stall watchdog will already
          // have surfaced the "prend plus de temps que prévu" UI.
          if (!pollSafetyTimer) {
            pollSafetyTimer = setTimeout(() => {
              if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
            }, 180_000);
          }
        };

        es.onopen = () => {
          setState((s) => ({ ...s, connectionState: "live" }));
          if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        };
        // EventSource.onerror = déconnexion réseau OU event SSE non-typé.
        // Ce n'est PAS une erreur fatale : on bascule en polling et on garde l'UI calme.
        es.onerror = () => {
          if (completed) return;
          setState((s) => ({ ...s, connectionState: "lost" }));
          startPolling();
          // Tentative immédiate
          void pollStatus();
        };

        // Soft-fails backend (Détective invalid_json, DeepSeek fallback prose,
        // dissenting note recovered, etc.) : on log, on n'affiche RIEN.
        es.addEventListener("warning", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          // eslint-disable-next-line no-console
          console.warn("[deliberation] soft warning", data);
        });
        es.addEventListener("error", (ev) => {
          // Event SSE nommé "error" envoyé par le backend.
          // Distinct de l'event natif EventSource.onerror (qui n'a pas de .data).
          const me = ev as MessageEvent;
          if (typeof me.data !== "string") return;
          const data = (safeParse(me.data) ?? {}) as Record<string, unknown>;
          if (data.severity === "fatal") {
            handleFatal(
              "error:fatal",
              typeof data.reason === "string" ? data.reason : undefined,
            );
            return;
          }
          // eslint-disable-next-line no-console
          console.warn("[deliberation] backend soft error", data);
        });

        // Soft-fail backend events — log only, no UI error.
        for (const evName of ["fallback", "agent_failed", "groq_invalid_json"] as const) {
          es.addEventListener(evName, (ev) => {
            // eslint-disable-next-line no-console
            console.warn(`[deliberation] soft ${evName}`, safeParse((ev as MessageEvent).data));
          });
        }

        // Fatal backend events — silent retry si tôt après ouverture (race
        // relance), sinon stall UI.
        for (const evName of ["fatal_error", "pipeline_aborted", "conversation_failed"] as const) {
          es.addEventListener(evName, (ev) => {
            const data = (safeParse((ev as MessageEvent).data) ?? {}) as Record<string, unknown>;
            // eslint-disable-next-line no-console
            console.error(`[deliberation] fatal ${evName}`, data);
            handleFatal(evName, typeof data.reason === "string" ? data.reason : undefined);
          });
        }

        // Keep-alive backend (named SSE event `ping`) — purement un signe de vie.
        // RESET du watchdog stall, AUCUN effet visuel, AUCUN message enregistré.
        // Émis notamment pendant la rédaction du verdict (~84s) et du mémo (~85s)
        // où le stream est légitimement silencieux.
        es.addEventListener("ping", () => {
          armStall();
        });

        es.addEventListener("deliberation_started", () => {
          armStall();
          setState((s) => ({ ...s, currentPhase: "summon", connectionState: "live" }));
        });

        es.addEventListener("round_start", (ev) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data) ?? {};
          setState((s) => ({
            ...s,
            currentPhase: "debate",
            currentTurn: safeNumber(data.round, s.currentTurn),
          }));
        });

        es.addEventListener("round_end", (ev) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data) ?? {};
          // Rendu immédiat — pas de file d'attente à flusher côté front.
          if (data.round === 2) {
            setState((s) => ({ ...s, currentPhase: "converge" }));
          }
        });

        const handleBubbleEvent = (ev: Event) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          // Le payload SSE peut nommer le conseiller de plusieurs façons selon
          // l'event/agent backend. On essaie toutes les variantes connues avant
          // de tomber dans le fallback "delphine".
          const rawAgent =
            safeString(data.persona) ||
            safeString(data.agent) ||
            safeString(data.agent_name) ||
            safeString(data.aiName) ||
            safeString(data.ai_name) ||
            safeString(data.ai) ||
            safeString(data.speaker) ||
            safeString(data.author) ||
            safeString(data.role) ||
            safeString(data.llm) ||
            safeString(data.model);
          // Invité (8e siège) : la key `expert-<uuid8>` doit traverser tel quel,
          // pas être mappée à un conseiller du Conseil.
          const guestKey = isGuestKey(rawAgent) ? rawAgent.toLowerCase() : null;
          const resolved = guestKey ? null : resolvePersona(rawAgent);
          if (!guestKey && !resolved) {
            // eslint-disable-next-line no-console
            console.warn("[live-stream] persona unresolved, defaulting", { raw_data: data, rawAgent });
          } else if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.debug("[live-stream] bubble", { rawAgent, resolved: guestKey ?? resolved, type: (ev as MessageEvent).type });
          }
          const persona: PersonaKey = guestKey ?? resolved ?? "delphine";
          const text: string = normalizeBubbleContent(data.content ?? data.text ?? data.message);
          const round = data.round === undefined ? undefined : safeNumber(data.round, 1);
          const trustworthiness = typeof data.trustworthiness === "number" ? data.trustworthiness : undefined;
          const time = nowHHMM();
          const msgId = `sse-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          // Rendu immédiat : le backend pace déjà les events (BUBBLE_PACING_MS=1200ms).
          // Pas de phase "typing" placeholder côté front — sinon les "..." restent
          // affichés et le texte ne devient jamais visible (bug S30-FE-BUBBLES-RENDER).
          setState((s) => ({
            ...s,
            bubbles: s.bubbles.map((b) =>
              b.persona === persona
                ? { ...b, state: "complete", time, text, round, trustworthiness }
                : b,
            ),
            messages: [
              ...s.messages,
              {
                id: msgId,
                persona,
                state: "complete",
                text,
                time,
                round,
                trustworthiness,
              },
            ],
          }));
        };

        es.addEventListener("message", handleBubbleEvent);
        // Variantes d'events backend qui portent aussi une bulle conseiller.
        for (const evName of ["bubble_chunk", "bubble_complete", "council_message"] as const) {
          es.addEventListener(evName, handleBubbleEvent);
        }

        // 8e siège — invité ponctuel (hors-champ). Même canal SSE que les
        // experts custom. On enregistre le GuestInfo dans state.guests pour
        // que la sidebar et les bulles puissent l'afficher comme un convive.
        es.addEventListener("expert_joined", (ev) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const guest = guestFromPayload(data);
          if (!guest) {
            // eslint-disable-next-line no-console
            console.warn("[deliberation] expert_joined payload invalide", data);
            return;
          }
          setState((s) =>
            s.guests[guest.key]
              ? s
              : { ...s, guests: { ...s.guests, [guest.key]: guest } },
          );
        });

        // Indicator "X écrit..." — backend peut émettre agent_speaking sans bulle.
        es.addEventListener("agent_speaking", (ev) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const rawAgent =
            safeString(data.persona) ||
            safeString(data.agent) ||
            safeString(data.agent_name) ||
            safeString(data.aiName) ||
            safeString(data.ai_name) ||
            safeString(data.ai) ||
            safeString(data.speaker) ||
            safeString(data.llm) ||
            safeString(data.model);
          const resolved = resolvePersona(rawAgent);
          if (!resolved) return;
          setState((s) => ({
            ...s,
            bubbles: s.bubbles.map((b) =>
              b.persona === resolved ? { ...b, state: "typing", time: nowHHMM() } : b,
            ),
          }));
        });

        // Cadre absent ce tour — marqueur sobre, jamais d'erreur visible.
        es.addEventListener("failed_intervention", (ev) => {
          armStall();
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const rawAgent =
            safeString(data.ai) ||
            safeString(data.persona) ||
            safeString(data.agent) ||
            safeString(data.agent_name) ||
            safeString(data.ai_name) ||
            safeString(data.aiName) ||
            safeString(data.speaker);
          const resolved = resolvePersona(rawAgent);
          if (!resolved) {
            // eslint-disable-next-line no-console
            console.warn("[deliberation] failed_intervention persona unresolved", data);
            return;
          }
          const round = data.round === undefined ? undefined : safeNumber(data.round, 1);
          const time = nowHHMM();
          const msgId = `abst-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          setState((s) => ({
            ...s,
            bubbles: s.bubbles.map((b) =>
              b.persona === resolved
                ? { ...b, state: "complete", time, text: "", round, abstained: true }
                : b,
            ),
            messages: [
              ...s.messages,
              {
                id: msgId,
                persona: resolved,
                state: "complete",
                text: "",
                time,
                round,
                abstained: true,
              },
            ],
          }));
        });



        es.addEventListener("research_started", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const search: SearchEvent = {
            id: `search-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            persona: "julien",
            query: safeString(data.query) || undefined,
            status: "running",
            timestamp: new Date().toISOString(),
          };
          setState((s) => ({ ...s, searches: [...s.searches, search] }));
        });

        // ── Requêtes de recherche live (défilé) ───────────────────────────
        es.addEventListener("research_query", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const query = safeString(data.query);
          if (!query) return;
          const source = (safeString(data.source) || "article") as ResearchQuery["source"];
          const lang = safeString(data.lang) || undefined;
          const entry: ResearchQuery = {
            id: `rq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            source,
            query,
            lang,
            receivedAt: Date.now(),
          };
          setState((s) => ({ ...s, researchQueries: [...s.researchQueries, entry] }));
        });



        const addInvestigationSource = (src: InvestigationSource) => {
          setState((s) => {
            if (s.investigationSources.some((x) => x.url === src.url)) return s;
            const sources = [...s.investigationSources, src];
            const byCategory = { ...s.investigationProgress.byCategory };
            byCategory[src.category] = (byCategory[src.category] ?? 0) + 1;
            // Si le backend n'a pas encore émis research_progress, on tient un compteur local.
            const localTotal = Math.max(s.investigationProgress.total, sources.length);
            return {
              ...s,
              investigationSources: sources,
              investigationProgress: { total: localTotal, byCategory },
            };
          });
        };

        // ── Source legacy (sans variant typé) ──────────────────────────────
        es.addEventListener("research_source", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const url = safeString(data.url);
          if (!url) return;
          const search: SearchEvent = {
            id: `source-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            persona: "julien",
            source_url: url,
            source_title: safeString(data.title, url) || undefined,
            snippet: safeString(data.snippet) || undefined,
            status: "done",
            timestamp: new Date().toISOString(),
          };
          setState((s) => ({ ...s, searches: [...s.searches, search] }));
          addInvestigationSource({
            id: `inv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            category: safeString(data.category, "article") || "article",
            url,
            title: safeString(data.title, url),
            receivedAt: Date.now(),
          });
        });

        // ── Variantes typées : on dérive la plateforme du nom d'event ──────
        const SOURCE_VARIANTS: ReadonlyArray<{
          event: string;
          platform: NonNullable<InvestigationSource["platform"]>;
          category: string;
        }> = [
          { event: "research_source_article", platform: "article", category: "article" },
          { event: "research_source_youtube", platform: "youtube", category: "video_youtube" },
          { event: "research_source_academic_paper", platform: "scholar", category: "academic_paper" },
          { event: "research_source_social_tiktok", platform: "tiktok", category: "social_tiktok" },
          { event: "research_source_social_instagram", platform: "instagram", category: "social_instagram" },
          { event: "research_source_social_pinterest", platform: "pinterest", category: "social_pinterest" },
          { event: "research_source_pinterest", platform: "pinterest", category: "social_pinterest" },
          { event: "research_source_social_x", platform: "x", category: "social_x" },
          { event: "research_source_linkedin", platform: "linkedin", category: "linkedin" },
          { event: "research_source_social_linkedin", platform: "linkedin", category: "social_linkedin" },
          { event: "research_source_juridique", platform: "juridique", category: "juridique" },
        ];

        for (const variant of SOURCE_VARIANTS) {
          es.addEventListener(variant.event, (ev) => {
            const data = safeParse((ev as MessageEvent).data);
            if (!data) return;
            const url = safeString(data.url);
            if (!url) return;
            const meta = (data.metadata && typeof data.metadata === "object")
              ? (data.metadata as Record<string, unknown>)
              : {};
            addInvestigationSource({
              id: `inv-${variant.platform}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              category: safeString(data.category, variant.category) || variant.category,
              platform: variant.platform,
              url,
              title: safeString(data.title, url),
              thumbnail: safeString(data.thumbnail) || safeString(meta.thumbnail) || undefined,
              metadata: {
                channel: safeString(data.channel) || safeString(meta.channel) || undefined,
                duration: safeString(data.duration) || safeString(meta.duration) || undefined,
                views:
                  typeof data.views === "number"
                    ? data.views
                    : safeString(data.views) || (typeof meta.views === "number" ? meta.views : safeString(meta.views)) || undefined,
                domain: safeString(data.domain) || safeString(meta.domain) || undefined,
              },
              receivedAt: Date.now(),
            });
          });
        }

        es.addEventListener("research_progress", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const total = safeNumber(data.total_sources_found ?? data.total, 0);
          const rawCat = (data.by_category && typeof data.by_category === "object")
            ? data.by_category as Record<string, unknown>
            : {};
          const byCategory: Record<string, number> = {};
          for (const [k, v] of Object.entries(rawCat)) {
            byCategory[k] = safeNumber(v, 0);
          }
          const rawStatus = safeString(data.status);
          const status: "researching" | "complete" | undefined =
            rawStatus === "complete" ? "complete"
            : rawStatus === "researching" ? "researching"
            : undefined;
          setState((s) => ({
            ...s,
            investigationProgress: {
              total: Math.max(total, s.investigationProgress.total),
              byCategory: { ...s.investigationProgress.byCategory, ...byCategory },
              status: status ?? s.investigationProgress.status,
            },
          }));
        });

        // ─── persona_research_* : recherches par cadre (indexées sur la clé modèle) ───
        const ensurePersonaEntry = (
          map: Record<string, PersonaResearch>,
          key: string,
          name = "",
          lens = "",
        ): PersonaResearch => {
          const existing = map[key];
          if (existing) {
            return {
              ...existing,
              name: existing.name || name,
              lens: existing.lens || lens,
            };
          }
          return { name, lens, queries: [], sources: [], done: false };
        };

        es.addEventListener("persona_research_started", (ev) => {
          const data = (safeParse((ev as MessageEvent).data) ?? {}) as {
            personas?: Array<{ key?: unknown; name?: unknown; lens?: unknown }>;
          };
          const personas = Array.isArray(data.personas) ? data.personas : [];
          if (personas.length === 0) return;
          setState((s) => {
            const next = { ...s.personaResearch };
            for (const p of personas) {
              const key = safeString(p?.key);
              if (!key) continue;
              next[key] = ensurePersonaEntry(
                next,
                key,
                safeString(p?.name),
                safeString(p?.lens),
              );
            }
            return { ...s, personaResearch: next };
          });
        });

        es.addEventListener("persona_research_query", (ev) => {
          const data = (safeParse((ev as MessageEvent).data) ?? {}) as {
            persona?: unknown;
            persona_name?: unknown;
            query?: unknown;
          };
          const key = safeString(data.persona);
          const query = safeString(data.query);
          if (!key || !query) return;
          setState((s) => {
            const entry = ensurePersonaEntry(
              s.personaResearch,
              key,
              safeString(data.persona_name),
            );
            return {
              ...s,
              personaResearch: {
                ...s.personaResearch,
                [key]: { ...entry, queries: [...entry.queries, query] },
              },
            };
          });
        });

        es.addEventListener("persona_research_source", (ev) => {
          const data = (safeParse((ev as MessageEvent).data) ?? {}) as {
            persona?: unknown;
            persona_name?: unknown;
            url?: unknown;
            title?: unknown;
            snippet?: unknown;
            score?: unknown;
          };
          const key = safeString(data.persona);
          const url = safeString(data.url);
          if (!key || !url) return;
          const source: PersonaResearchSource = {
            url,
            title: safeString(data.title, url),
            snippet: safeString(data.snippet),
            score: safeNumber(data.score, 0),
          };
          setState((s) => {
            const entry = ensurePersonaEntry(
              s.personaResearch,
              key,
              safeString(data.persona_name),
            );
            return {
              ...s,
              personaResearch: {
                ...s.personaResearch,
                [key]: { ...entry, sources: [...entry.sources, source] },
              },
            };
          });
        });

        es.addEventListener("persona_research_done", (ev) => {
          const data = (safeParse((ev as MessageEvent).data) ?? {}) as {
            by_persona?: Record<string, unknown>;
          };
          const byPersona =
            data.by_persona && typeof data.by_persona === "object"
              ? data.by_persona
              : {};
          setState((s) => {
            const next: Record<string, PersonaResearch> = { ...s.personaResearch };
            const keys = new Set<string>([
              ...Object.keys(next),
              ...Object.keys(byPersona),
            ]);
            for (const key of keys) {
              const entry = ensurePersonaEntry(next, key);
              next[key] = { ...entry, done: true };
            }
            return { ...s, personaResearch: next };
          });
        });



        es.addEventListener("paused", () => {
          setState((s) => ({ ...s, currentPhase: "summon" }));
        });

        // ─── Vérif : fil d'activité du Conseil (débat interne, pas de bulles) ───
        es.addEventListener("verif_activity", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const cadre = safeString(data.cadre);
          const sujet = safeString(data.sujet);
          if (!cadre || !sujet) return;
          const angle = safeString(data.angle);
          const pointsReleves = safeNumber(data.points_releves, 0);
          setState((s) => {
            const last = s.verifActivities[s.verifActivities.length - 1];
            if (last && last.cadre === cadre && last.sujet === sujet) {
              // Dédup consécutif : on met juste à jour le compteur.
              return {
                ...s,
                verifActivities: [
                  ...s.verifActivities.slice(0, -1),
                  { ...last, pointsReleves: Math.max(last.pointsReleves, pointsReleves) },
                ],
              };
            }
            return {
              ...s,
              verifActivities: [
                ...s.verifActivities,
                {
                  id: `va-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                  cadre,
                  angle,
                  sujet,
                  pointsReleves,
                  receivedAt: Date.now(),
                },
              ],
            };
          });
        });

        // ─── Vérif : grounding juridique (consultation droit) ───
        es.addEventListener("verif_grounding_legal", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const articles = safeNumber(data.articles, 0);
          const decisions = safeNumber(data.decisions, 0);
          if (articles <= 0 && decisions <= 0) return; // pas de droit sur ce dossier
          setState((s) => ({ ...s, verifGroundingLegal: { articles, decisions } }));
        });

        // ─── Vérif : fact-check web ───
        es.addEventListener("verif_factcheck", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const verifies = safeNumber(data.verifies, 0);
          const confirmees = safeNumber(data.confirmees, 0);
          if (verifies <= 0) return;
          setState((s) => ({ ...s, verifFactcheck: { verifies, confirmees } }));
        });

        // ─── Vérif : confrontation entre cadres ───
        es.addEventListener("verif_exchange", (ev) => {
          const data = safeParse((ev as MessageEvent).data);
          if (!data) return;
          const from = safeString(data.from);
          const to = safeString(data.to);
          if (!from || !to) return;
          const pointsConfrontes = safeNumber(data.points_confrontes, 0);
          setState((s) => {
            const last = s.verifExchanges[s.verifExchanges.length - 1];
            if (last && last.from === from && last.to === to) {
              return {
                ...s,
                verifExchanges: [
                  ...s.verifExchanges.slice(0, -1),
                  {
                    ...last,
                    pointsConfrontes: Math.max(last.pointsConfrontes, pointsConfrontes),
                    receivedAt: Date.now(),
                  },
                ],
              };
            }
            return {
              ...s,
              verifExchanges: [
                ...s.verifExchanges,
                {
                  id: `vx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                  from,
                  to,
                  pointsConfrontes,
                  receivedAt: Date.now(),
                },
              ],
            };
          });
        });

        es.addEventListener("resumed", () => {
          setState((s) => ({ ...s, currentPhase: "debate" }));
        });

        // Event `clarity_ready` : le verdict structuré (verdict_clarity_json)
        // est persisté côté backend. On révèle le verdict court IMMÉDIATEMENT
        // sans attendre `complete` (bloqué ~80 s par la génération du mémo).
        es.addEventListener("clarity_ready", (ev) => {
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const vId = safeString(data.id, id);
          setState((s) => ({
            ...s,
            verdictId: s.verdictId ?? vId,
            verdictReady: true,
            clarityReady: true,
            currentPhase: "verdict",
          }));
        });

        // Event `verdict` : surfacer le verdict immédiatement, sans attendre
        // meeting_done ni un payload "clarity" structuré. Le cockpit dérive
        // `debateFinished` de currentPhase === "verdict" → CTA Mémo visible.
        es.addEventListener("verdict", (ev) => {
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const vId = safeString(data.id, id);
          setState((s) => ({
            ...s,
            verdictId: vId,
            verdictReady: true,
            currentPhase: "verdict",
            isCompleted: true,
          }));
        });


        // Event `meeting_done` : toutes les bulles ont été émises.
        // S31 Phase 2-ter : plus de redirect auto. L'user clique sur le
        // CTA "Demander le Mémo du Conseil" dans le cockpit V3.1 pour
        // naviguer vers /verdict/:id. Comportement unique et prévisible.
        es.addEventListener("meeting_done", (ev) => {
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const vId = safeString(data.id, id) || (typeof window !== "undefined" ? (id as string) : id);
          const clarityReady = Boolean(data.clarityReady ?? data.clarity_ready);
          setState((s) => ({
            ...s,
            meetingDone: true,
            verdictReady: true,
            verdictId: s.verdictId ?? vId,
            clarityReady,
            currentPhase: "verdict",
          }));
        });

        // Event `memo_ready` : le mémo est disponible. On garantit ici que
        // verdictReady/clarityReady/currentPhase basculent bien (parité totale
        // avec la fin d'un débat initial), même si l'event `clarity_ready`
        // aurait été manqué en amont — utile notamment sur les relances.
        es.addEventListener("memo_ready", (ev) => {
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const vId = safeString(data.id, id);
          setState((s) => ({
            ...s,
            verdictId: s.verdictId ?? vId,
            verdictReady: true,
            clarityReady: true,
            currentPhase: "verdict",
          }));
        });


        // Signal legacy de fin pipeline. S30 : il ne doit plus rediriger.
        // Seul `meeting_done` est autorisé à déclencher le redirect auto.
        es.addEventListener("complete_conversation_done", (ev) => {
          completed = true;
          stopWatchdogs();
          const data = safeParse((ev as MessageEvent).data) ?? {};
          const vId = safeString(data.id, id);
          // S31 strict gating : `complete_conversation_done` (alias "complete" backend)
          // arrive ~150 s après le verdict réel. Il ne doit PAS révéler le CTA verdict.
          // Seuls les events `verdict` et `meeting_done` mettent `verdictReady=true`.
          setState((s) => ({
            ...s,
            isCompleted: true,
            verdictId: s.verdictId ?? vId,
          }));
          es?.close();
        });

        es.addEventListener("complete", () => {
          completed = true;
          stopWatchdogs();
          es?.close();
        });

        // Expose cleanup pour le return du useEffect via fermeture
        es.__cleanup__ = stopWatchdogs;
      } catch {
        if (!cancelled) {
          // Erreur d'initialisation (token, history…) : on tente le polling, pas Erreur 500.
          setState((s) => ({ ...s, connectionState: "lost" }));
        }
      }
    })();

    return () => {
      cancelled = true;
      es?.__cleanup__?.();
      es?.close();
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [id, navigate, archiveMode, reconnectNonce]);

  // ─── A11y announce() : moments significatifs uniquement ───
  const prevPhaseRef = useRef<OrbPhase>(state.currentPhase);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (prev === state.currentPhase) return;
    prevPhaseRef.current = state.currentPhase;
    switch (state.currentPhase) {
      case "summon":
        announce("Le Conseil convoque ses membres", "polite");
        break;
      case "debate":
        announce(
          `Le Conseil entame la délibération, tour ${state.currentTurn || 1}`,
          "polite",
        );
        break;
      case "converge":
        announce("Le Conseil converge vers le verdict", "polite");
        break;
      case "verdict":
        announce("Le verdict du Conseil est rendu", "assertive");
        break;
    }
  }, [state.currentPhase, state.currentTurn]);

  const prevConnRef = useRef<ConnectionState>(state.connectionState);
  useEffect(() => {
    const prev = prevConnRef.current;
    if (prev === state.connectionState) return;
    prevConnRef.current = state.connectionState;
    if (state.connectionState === "lost") {
      announce("Connexion perdue au Conseil", "assertive");
    } else if (state.connectionState === "live" && prev === "lost") {
      announce("Connexion rétablie", "polite");
    }
  }, [state.connectionState]);

  const prevBubbleStatesRef = useRef<Record<string, BubbleState>>({});
  useEffect(() => {
    const prev = prevBubbleStatesRef.current;
    const next: Record<string, BubbleState> = {};
    for (const b of state.bubbles) {
      next[b.persona] = b.state;
      const was = prev[b.persona];
      if (was === "typing" && (b.state === "complete" || b.state === "streaming")) {
        const name = PERSONA_NAME_BY_ID[b.persona] ?? b.persona;
        announce(`${name} prend la parole`, "polite");
      }
    }
    prevBubbleStatesRef.current = next;
  }, [state.bubbles]);

  return { ...state, displayedMessages, displayedCount, queuedCount, pace, setPace, accelerate, flushQueue, reconnect };
}

export { TURN_ORDER };
export const COUNCIL_BY_ID = Object.fromEntries(COUNCIL.map((c) => [c.id, c]));
